/**
 * The rooms this browser has already graded, on /rate's front door.
 *
 * 🔴 IT IS THE OTHER HALF OF "RATE ANOTHER LEAGUE". Letting somebody start a
 * second room is only kind if the first one is still somewhere; without this
 * list the button would be a delete key with a friendly label. So the two ship
 * together: the room saves itself as it grades, and this is where it comes back.
 *
 * 🔴 IT DOES NOT SAY "YOUR LEAGUES", even though that is what it is. The team
 * tab's settings step already has a card by that name holding the Team
 * Builder's saved league SETTINGS, and those are a different thing: a league
 * you set up versus a draft you graded. Two cards with one heading is how a
 * user learns to distrust both.
 *
 * WITH NOTHING SAVED THE CARD IS ABSENT ENTIRELY — no empty state, no "you have
 * not rated a league yet". A first-time visitor is here to paste a draft, and a
 * box telling them what they do not have is friction with no payoff. The same
 * rule the saved-league picker follows one file over.
 */

import { theme } from "../../data/theme.js";
import { C, SZ, card, fineText, leagueNameText, sectionTitle } from "../Submit/submitStyles.js";

/** '1qb' | 'sflex' | '2qb' as a reader sees it. */
export function formatWord(format) {
  if (format === "sflex") return "Superflex";
  if (format === "2qb") return "2QB";
  return "1QB";
}

/**
 * When it was rated, short.
 *
 * Month and day only: these are this season's drafts and a year on every chip
 * is a column of "2026" telling nobody anything. A record with no timestamp —
 * an older write, or a hand-seeded key — gets nothing rather than "Dec 31 1969".
 */
export function savedWhen(ms) {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function SavedRoomsPicker({ rooms, onOpen, onRemove }) {
  if (!rooms || rooms.length === 0) return null;
  return (
    <div style={{ ...card, marginBottom: 12 }} data-testid="saved-rooms-picker">
      {/* Promoted from a small-caps eyebrow (Ben, Sep 3 2026). This is a
          section of the landing, not a caption on a card, and it is the section
          a returning reader is looking for first. */}
      <div data-testid="saved-rooms-title" style={sectionTitle}>
        Your rated leagues
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rooms.map((r) => (
          <div
            key={r.id}
            data-testid={`saved-room-${r.id}`}
            style={{
              display: "flex",
              alignItems: "stretch",
              gap: 6,
              border: `1px solid ${C.border.default}`,
              borderRadius: theme.radius.lg,
              overflow: "hidden",
            }}
          >
            {/* 🔴 THE OPEN AFFORDANCE AND THE REMOVE ONE ARE SIBLINGS, NOT
                NESTED. A button inside a button is invalid markup and, worse,
                gives the remove tap two handlers to fight over — which on a
                phone is how somebody deletes the league they meant to open. */}
            <button
              type="button"
              data-testid={`saved-room-open-${r.id}`}
              onClick={() => onOpen && onOpen(r)}
              style={{
                flex: "1 1 auto",
                textAlign: "left",
                padding: "10px 12px",
                border: "none",
                background: "transparent",
                color: C.text.primary,
                cursor: "pointer",
                minWidth: 0,
              }}
            >
              {/* 🔴 THE WHOLE LABEL, NOT JUST THE NAMED CASE. On the report
                  heading the accent marks a name inside a line of metadata, so
                  the unnamed fallback stays grey there. A chip is different: the
                  label IS the row, whether it reads "Strong Island Fantasy" or
                  "10-team sflex $200", and colouring half the list would read as
                  a status difference between the leagues that does not exist. */}
              <div
                data-testid={`saved-room-label-${r.id}`}
                style={{
                  ...leagueNameText,
                  fontSize: SZ.small,
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r.label}
              </div>
              <div style={{ fontSize: SZ.micro, color: C.text.secondary, marginTop: 2 }}>
                {formatWord(r.meta.format)} · {Number(r.meta.teams) || r.state.teams.length} teams
                {r.meta.budget ? ` · $${r.meta.budget}` : ""}
                {savedWhen(r.savedAt) ? ` · ${savedWhen(r.savedAt)}` : ""}
              </div>
            </button>
            {/* One tap, no dialog. What it removes is a local copy of a league
                the user can paste again in ten seconds; a confirm here would
                cost more taps over a year than the mistake it prevents. */}
            <button
              type="button"
              aria-label={`Remove ${r.label}`}
              data-testid={`saved-room-remove-${r.id}`}
              onClick={() => onRemove && onRemove(r.id)}
              style={{
                flex: "0 0 auto",
                padding: "0 14px",
                border: "none",
                borderLeft: `1px solid ${C.border.default}`,
                background: "transparent",
                color: C.text.faint,
                fontSize: 16,
                lineHeight: 1,
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <div style={fineText}>
        Kept on this device only. Opening one shows the room exactly as it graded.
      </div>
    </div>
  );
}
